import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import Icon from '../../components/AppIcon';

const AuthBackground = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
    style={{ background: 'linear-gradient(135deg, #e8e4f8 0%, #d4d8f5 25%, #c8d4f0 50%, #b8cef0 75%, #a8c8ee 100%)' }}>
    <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
      <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 320" preserveAspectRatio="none" style={{height:'45%'}}>
        <path fill="rgba(100,120,220,0.18)" d="M0,160L60,170.7C120,181,240,203,360,197.3C480,192,600,160,720,154.7C840,149,960,171,1080,181.3C1200,192,1320,192,1380,192L1440,192L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"/>
      </svg>
      <svg className="absolute bottom-0 right-0 w-3/4" viewBox="0 0 800 400" preserveAspectRatio="none" style={{height:'55%'}}>
        <path fill="rgba(80,100,200,0.22)" d="M800,100L700,120C600,140,400,180,200,190C0,200,0,180,0,160L0,400L800,400Z"/>
      </svg>
      <div className="absolute top-8 right-12 opacity-20" style={{color:'#7c6fe0'}}>
        {[...Array(5)]?.map((_,r)=>(
          <div key={r} className="flex space-x-3 mb-3">
            {[...Array(5)]?.map((_,c)=><div key={c} className="w-1.5 h-1.5 rounded-full bg-current"/>)}
          </div>
        ))}
      </div>
    </div>
    <div className="w-full max-w-md relative z-10">
      {children}
    </div>
  </div>
);

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [form, setForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });

  const passwordRules = [
    { label: 'At least 12 characters', test: (p) => p?.length >= 12 },
    { label: 'At least 1 uppercase letter', test: (p) => /[A-Z]/?.test(p) },
    { label: 'At least 1 number', test: (p) => /[0-9]/?.test(p) },
    { label: 'At least 1 special character', test: (p) => /[^A-Za-z0-9]/?.test(p) },
  ];

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const failedRules = passwordRules?.filter(r => !r?.test(form?.new_password));
    if (failedRules?.length > 0) { setError('New password does not meet requirements'); return; }
    if (form?.new_password !== form?.confirm_password) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const userId = user?.id || user?.user_id;
      await apiService?.resetPassword(userId, {
        old_password: form?.old_password,
        new_password: form?.new_password,
      });
      setSuccess('Password reset successfully!');
      const redirectToOtpSetup = location?.state?.redirectToOtpSetup;
      const emailAddress = location?.state?.email_address;
      setTimeout(() => {
        if (redirectToOtpSetup) {
          navigate('/otp-setup', { state: { email_address: emailAddress } });
        } else {
          navigate('/dashboard');
        }
      }, 2000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthBackground>
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg"
          style={{ background: 'linear-gradient(135deg, #5b4fcf 0%, #4a6bc4 100%)' }}>
          <Icon name="KeyRound" size={30} color="white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Reset Password</h1>
        <p className="text-gray-500 mt-2 text-sm">Update your account password</p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-8 border border-white/60">
        {success ? (
          <div className="text-center py-8 space-y-4">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-green-50 rounded-full">
              <Icon name="CheckCircle" size={28} className="text-green-500" />
            </div>
            <p className="text-gray-800 font-semibold">{success}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Password Rules */}
            <div className="p-3 rounded-xl" style={{background:'#f5f3ff'}}>
              <p className="text-xs font-semibold mb-2" style={{color:'#5b4fcf'}}>Password Requirements:</p>
              <div className="space-y-1">
                {passwordRules?.map((rule, i) => (
                  <div key={i} className="flex items-center space-x-2">
                    <Icon name={rule?.test(form?.new_password) ? 'CheckCircle2' : 'Circle'} size={12}
                      className={rule?.test(form?.new_password) ? 'text-green-500' : 'text-gray-400'} />
                    <span className={`text-xs ${rule?.test(form?.new_password) ? 'text-green-600' : 'text-gray-500'}`}>{rule?.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {[['old_password','Current Password','current'],['new_password','New Password','new'],['confirm_password','Confirm New Password','confirm']]?.map(([name, label, key]) => (
              <div key={name} className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-800">{label}</label>
                <div className="relative">
                  <Icon name="Lock" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{color:'#5b4fcf'}} />
                  <input
                    type={showPasswords?.[key] ? 'text' : 'password'}
                    name={name}
                    value={form?.[name]}
                    onChange={handleChange}
                    placeholder={label}
                    className="w-full h-11 pl-10 pr-10 rounded-xl border border-[#dde1f5] bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5b4fcf]/20 focus:border-[#5b4fcf] transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(p => ({ ...p, [key]: !p?.[key] }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <Icon name={showPasswords?.[key] ? 'EyeOff' : 'Eye'} size={16} />
                  </button>
                </div>
              </div>
            ))}

            {error && (
              <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl text-sm font-semibold text-white shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center space-x-2 transition-all"
              style={{ background: 'linear-gradient(135deg, #5b4fcf 0%, #4a6bc4 100%)' }}
            >
              {loading
                ? <><Icon name="Loader2" size={16} className="animate-spin" /><span>Resetting...</span></>
                : <span>Reset Password</span>}
            </button>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-full text-sm text-gray-500 hover:text-gray-700 text-center transition-colors"
            >
              Cancel
            </button>
          </form>
        )}
      </div>
    </AuthBackground>
  );
};

export default ForgotPasswordPage;
